import { SupabaseClient } from '@supabase/supabase-js';

import { TABLES } from '../../../config/supabase';
import { AuthError, OnboardingProgress } from '../../../types';

/**
 * Onboarding progress service
 * Manages user onboarding steps and completion tracking
 */
export class OnboardingService {
  private supabase: SupabaseClient;

  constructor(supabaseClient: SupabaseClient) {
    this.supabase = supabaseClient;
  }

  /**
   * Transform database row (snake_case) to interface format (camelCase)
   */
  private transformOnboardingProgress(
    dbRow: Record<string, unknown>
  ): OnboardingProgress {
    return {
      id: dbRow.id as string,
      userId: dbRow.user_id as string,
      personalInfoCompleted: dbRow.personal_info_completed as boolean,
      riskAssessmentCompleted: dbRow.risk_assessment_completed as boolean,
      firstGoalCreated: dbRow.first_goal_created as boolean,
      firstAccountConnected: dbRow.first_account_connected as boolean,
      personalInfoCompletedAt: dbRow.personal_info_completed_at as
        | string
        | undefined,
      riskAssessmentCompletedAt: dbRow.risk_assessment_completed_at as
        | string
        | undefined,
      firstGoalCreatedAt: dbRow.first_goal_created_at as string | undefined,
      firstAccountConnectedAt: dbRow.first_account_connected_at as
        | string
        | undefined,
      currentStep: dbRow.current_step as string | undefined,
      completionPercentage: dbRow.completion_percentage as number,
      createdAt: dbRow.created_at as string,
      updatedAt: dbRow.updated_at as string,
    };
  }

  // ============================================================================
  // Onboarding Progress Management
  // ============================================================================

  async getOnboardingProgress(userId: string): Promise<{
    success: boolean;
    progress?: OnboardingProgress;
    error?: AuthError;
  }> {
    try {
      console.log('🔍 Fetching onboarding progress for:', userId);

      const { data, error } = await this.supabase
        .from(TABLES.ONBOARDING_PROGRESS)
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(1);

      if (error) {
        console.log('❌ Error fetching onboarding progress:', error.message);
        return {
          success: false,
          error: {
            code: 'DATABASE_ERROR',
            message: error.message,
          },
        };
      }

      if (!data || data.length === 0) {
        console.log(
          'ℹ️ No onboarding progress found for user, creating initial record'
        );

        // Initialize onboarding progress
        const { data: newProgress, error: initError } = await this.supabase
          .from(TABLES.ONBOARDING_PROGRESS)
          .insert({
            user_id: userId,
            current_step: 'personal_info',
            completion_percentage: 0,
            personal_info_completed: false,
            risk_assessment_completed: false,
            first_goal_created: false,
            first_account_connected: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (initError || !newProgress) {
          console.log(
            '❌ Error creating initial onboarding progress:',
            initError
          );
          return {
            success: false,
            error: {
              code: 'NOT_FOUND',
              message: 'No onboarding progress found and failed to initialize',
            },
          };
        }

        console.log('✅ Initial onboarding progress created');
        return {
          success: true,
          progress: this.transformOnboardingProgress(newProgress),
        };
      }

      const progress = data[0];
      console.log('✅ Onboarding progress found:', {
        completionPercentage: progress.completion_percentage,
        currentStep: progress.current_step,
      });

      return {
        success: true,
        progress: this.transformOnboardingProgress(progress),
      };
    } catch (error) {
      console.log('❌ Unexpected error fetching onboarding progress:', error);
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  async updateOnboardingStep(
    userId: string,
    step: 'personal_info' | 'risk_assessment' | 'first_goal' | 'first_account',
    completed: boolean = true,
    updateUserStatus?: (
      userId: string,
      updates: Record<string, unknown>
    ) => Promise<void>
  ): Promise<{
    success: boolean;
    progress?: OnboardingProgress;
    error?: AuthError;
  }> {
    try {
      console.log(
        `🔄 Updating onboarding step '${step}' to ${completed} for:`,
        userId
      );

      // Get existing progress
      const currentProgress = await this.getOnboardingProgress(userId);
      if (!currentProgress.success || !currentProgress.progress) {
        return {
          success: false,
          error: currentProgress.error,
        };
      }

      const existingProgress = currentProgress.progress;

      const updateData: Record<string, unknown> = {
        user_id: userId,
        updated_at: new Date().toISOString(),
      };

      const timestampField = `${step}_completed_at`;
      const completedField = `${step}_completed`;

      updateData[completedField] = completed;
      if (completed) {
        updateData[timestampField] = new Date().toISOString();
      } else {
        updateData[timestampField] = null;
      }

      // Calculate completion percentage
      const progress = existingProgress;
      const steps = [
        progress.personalInfoCompleted ||
          (step === 'personal_info' && completed),
        progress.riskAssessmentCompleted ||
          (step === 'risk_assessment' && completed),
        progress.firstGoalCreated || (step === 'first_goal' && completed),
        progress.firstAccountConnected ||
          (step === 'first_account' && completed),
      ];

      const completedSteps = steps.filter(Boolean).length;
      updateData.completion_percentage = Math.round((completedSteps / 4) * 100);

      // Update current step
      if (completed) {
        const stepOrder = [
          'personal_info',
          'risk_assessment',
          'first_goal',
          'first_account',
        ];
        const currentStepIndex = stepOrder.indexOf(step);
        const nextStepIndex = currentStepIndex + 1;

        if (nextStepIndex < stepOrder.length && !steps[nextStepIndex]) {
          updateData.current_step = stepOrder[nextStepIndex];
        } else if (completedSteps === 4) {
          updateData.current_step = 'completed';
        }
      }

      // Update existing record
      const { data, error } = await this.supabase
        .from(TABLES.ONBOARDING_PROGRESS)
        .update(updateData)
        .eq('id', existingProgress.id)
        .select()
        .single();

      if (error) {
        console.log('❌ Error updating onboarding step:', error.message);
        return {
          success: false,
          error: {
            code: 'DATABASE_ERROR',
            message: error.message,
          },
        };
      }

      console.log('✅ Onboarding step updated successfully');

      // If onboarding is complete, update user profile status
      if (data.completion_percentage === 100 && updateUserStatus) {
        console.log('🎉 Onboarding completed, updating user status to active');
        await updateUserStatus(userId, {
          status: 'active',
          onboarding_completed_at: new Date().toISOString(),
        });
      }

      return {
        success: true,
        progress: this.transformOnboardingProgress(data),
      };
    } catch (error) {
      console.log('❌ Unexpected error updating onboarding step:', error);
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  async resetOnboardingProgress(userId: string): Promise<{
    success: boolean;
    error?: AuthError;
  }> {
    try {
      console.log('🔄 Resetting onboarding progress for:', userId);

      const { error } = await this.supabase
        .from(TABLES.ONBOARDING_PROGRESS)
        .update({
          personal_info_completed: false,
          risk_assessment_completed: false,
          first_goal_created: false,
          first_account_connected: false,
          personal_info_completed_at: null,
          risk_assessment_completed_at: null,
          first_goal_created_at: null,
          first_account_connected_at: null,
          current_step: 'personal_info',
          completion_percentage: 0,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId);

      if (error) {
        console.log('❌ Error resetting onboarding progress:', error.message);
        return {
          success: false,
          error: {
            code: 'DATABASE_ERROR',
            message: error.message,
          },
        };
      }

      console.log('✅ Onboarding progress reset successfully');
      return { success: true };
    } catch (error) {
      console.log('❌ Unexpected error resetting onboarding progress:', error);
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  async validateOnboardingStep(
    userId: string,
    step: 'personal_info' | 'risk_assessment' | 'first_goal' | 'first_account'
  ): Promise<{
    isValid: boolean;
    canProceed: boolean;
    missingRequirements?: string[];
    error?: AuthError;
  }> {
    try {
      console.log(`🔍 Validating onboarding step '${step}' for:`, userId);

      const progressResult = await this.getOnboardingProgress(userId);
      if (!progressResult.success || !progressResult.progress) {
        return {
          isValid: false,
          canProceed: false,
          error: progressResult.error,
        };
      }

      const progress = progressResult.progress;
      const missingRequirements: string[] = [];

      // Validate step prerequisites
      switch (step) {
        case 'personal_info':
          return { isValid: true, canProceed: true };

        case 'risk_assessment':
          if (!progress.personalInfoCompleted) {
            missingRequirements.push(
              'Personal information must be completed first'
            );
          }
          break;

        case 'first_goal':
          if (!progress.personalInfoCompleted) {
            missingRequirements.push(
              'Personal information must be completed first'
            );
          }
          if (!progress.riskAssessmentCompleted) {
            missingRequirements.push('Risk assessment must be completed first');
          }
          break;

        case 'first_account':
          if (!progress.personalInfoCompleted) {
            missingRequirements.push(
              'Personal information must be completed first'
            );
          }
          if (!progress.riskAssessmentCompleted) {
            missingRequirements.push('Risk assessment must be completed first');
          }
          if (!progress.firstGoalCreated) {
            missingRequirements.push('First goal must be created first');
          }
          break;

        default:
          return {
            isValid: false,
            canProceed: false,
            error: {
              code: 'INVALID_STEP',
              message: `Unknown onboarding step: ${step}`,
            },
          };
      }

      const isValid = missingRequirements.length === 0;
      console.log(
        `✅ Step validation result: ${isValid ? 'valid' : 'invalid'}`,
        { step, missingRequirements }
      );

      return {
        isValid,
        canProceed: isValid,
        missingRequirements:
          missingRequirements.length > 0 ? missingRequirements : undefined,
      };
    } catch (error) {
      console.log('❌ Unexpected error validating onboarding step:', error);
      return {
        isValid: false,
        canProceed: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  async getNextOnboardingStep(userId: string): Promise<{
    success: boolean;
    nextStep?: string;
    isComplete?: boolean;
    progress?: OnboardingProgress;
    error?: AuthError;
  }> {
    try {
      const progressResult = await this.getOnboardingProgress(userId);
      if (!progressResult.success || !progressResult.progress) {
        return {
          success: false,
          error: progressResult.error,
        };
      }

      const progress = progressResult.progress;

      // Determine next step
      if (!progress.personalInfoCompleted) {
        return {
          success: true,
          nextStep: 'personal_info',
          isComplete: false,
          progress,
        };
      }

      if (!progress.riskAssessmentCompleted) {
        return {
          success: true,
          nextStep: 'risk_assessment',
          isComplete: false,
          progress,
        };
      }

      if (!progress.firstGoalCreated) {
        return {
          success: true,
          nextStep: 'first_goal',
          isComplete: false,
          progress,
        };
      }

      if (!progress.firstAccountConnected) {
        return {
          success: true,
          nextStep: 'first_account',
          isComplete: false,
          progress,
        };
      }

      return {
        success: true,
        nextStep: 'completed',
        isComplete: true,
        progress,
      };
    } catch (error) {
      console.log('❌ Unexpected error getting next onboarding step:', error);
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  async canResumeOnboarding(userId: string): Promise<{
    canResume: boolean;
    resumeStep?: string;
    completionPercentage?: number;
    error?: AuthError;
  }> {
    try {
      // Check user profile status
      const { data: profile, error: profileError } = await this.supabase
        .from(TABLES.USER_PROFILES)
        .select('*')
        .eq('id', userId);

      if (profileError) {
        console.log('❌ User profile query error:', profileError);
        return {
          canResume: false,
          error: {
            code: 'DATABASE_ERROR',
            message: profileError.message,
          },
        };
      }

      if (!profile || profile.length === 0) {
        console.log('ℹ️ No user profile found');
        return {
          canResume: false,
          error: {
            code: 'DATABASE_ERROR',
            message: 'User profile not found',
          },
        };
      }

      const userProfile = profile[0];

      // Only allow resumption if user is in onboarding status
      if (userProfile.status !== 'onboarding') {
        return {
          canResume: false,
          error: {
            code: 'INVALID_STATUS',
            message: `Cannot resume onboarding. User status is '${userProfile.status}'`,
          },
        };
      }

      const nextStepResult = await this.getNextOnboardingStep(userId);
      if (!nextStepResult.success) {
        return {
          canResume: false,
          error: nextStepResult.error,
        };
      }

      return {
        canResume: !nextStepResult.isComplete,
        resumeStep: nextStepResult.nextStep,
        completionPercentage:
          nextStepResult.progress?.completionPercentage || 0,
      };
    } catch (error) {
      console.log('❌ Unexpected error checking onboarding resumption:', error);
      return {
        canResume: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  async isOnboardingComplete(
    userId: string,
    checkHasGoals: (userId: string) => Promise<boolean>
  ): Promise<boolean> {
    try {
      // Check user profile status - if active, onboarding is complete
      const { data: profile, error: profileError } = await this.supabase
        .from(TABLES.USER_PROFILES)
        .select('status')
        .eq('id', userId);

      if (
        !profileError &&
        profile &&
        profile.length > 0 &&
        profile[0]?.status === 'active'
      ) {
        return true;
      }

      // Check onboarding progress for core steps
      const progressResult = await this.getOnboardingProgress(userId);
      if (!progressResult.success || !progressResult.progress) {
        return false;
      }

      const progress = progressResult.progress;

      // Core onboarding is complete when both personal info and risk assessment are done
      const coreOnboardingComplete =
        progress.personalInfoCompleted && progress.riskAssessmentCompleted;

      if (coreOnboardingComplete) {
        return true;
      }

      // Legacy fallback: if user has goals, consider onboarding complete
      const hasGoals = await checkHasGoals(userId);
      return hasGoals;
    } catch (error) {
      console.log('❌ Error checking onboarding completion:', error);
      return false;
    }
  }

  async isCoreOnboardingComplete(userId: string): Promise<boolean> {
    try {
      // Check user profile status
      const { data: profile, error: profileError } = await this.supabase
        .from(TABLES.USER_PROFILES)
        .select('status')
        .eq('id', userId);

      if (
        !profileError &&
        profile &&
        profile.length > 0 &&
        profile[0]?.status === 'active'
      ) {
        return true;
      }

      // Check onboarding progress for core steps only
      const progressResult = await this.getOnboardingProgress(userId);
      if (!progressResult.success || !progressResult.progress) {
        return false;
      }

      const progress = progressResult.progress;

      // Core onboarding is complete when both personal info and risk assessment are done
      return progress.personalInfoCompleted && progress.riskAssessmentCompleted;
    } catch (error) {
      console.log('❌ Error checking core onboarding completion:', error);
      return false;
    }
  }

  /**
   * Get user status from profile
   * Returns: 'pending_verification' | 'onboarding' | 'active' | 'suspended' | null
   */
  async getUserStatus(
    userId: string
  ): Promise<
    'pending_verification' | 'onboarding' | 'active' | 'suspended' | null
  > {
    try {
      const { data: profile, error } = await this.supabase
        .from(TABLES.USER_PROFILES)
        .select('status')
        .eq('id', userId)
        .single();

      if (error || !profile) {
        console.log('❌ Error fetching user status:', error);
        return null;
      }

      return profile.status as
        | 'pending_verification'
        | 'onboarding'
        | 'active'
        | 'suspended';
    } catch (error) {
      console.log('❌ Unexpected error fetching user status:', error);
      return null;
    }
  }

  /**
   * Mark user as starting onboarding (pending_verification → onboarding)
   */
  async startOnboarding(userId: string): Promise<{
    success: boolean;
    error?: AuthError;
  }> {
    try {
      const { error } = await this.supabase
        .from(TABLES.USER_PROFILES)
        .update({
          status: 'onboarding',
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) {
        console.log('❌ Error starting onboarding:', error);
        return {
          success: false,
          error: {
            code: 'DATABASE_ERROR',
            message: error.message,
          },
        };
      }

      console.log('✅ User marked as onboarding');
      return { success: true };
    } catch (error) {
      console.log('❌ Unexpected error starting onboarding:', error);
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  /**
   * Mark user as active (onboarding → active)
   * Call this when user completes core onboarding steps
   */
  async completeOnboarding(userId: string): Promise<{
    success: boolean;
    error?: AuthError;
  }> {
    try {
      const { error } = await this.supabase
        .from(TABLES.USER_PROFILES)
        .update({
          status: 'active',
          onboarding_completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) {
        console.log('❌ Error completing onboarding:', error);
        return {
          success: false,
          error: {
            code: 'DATABASE_ERROR',
            message: error.message,
          },
        };
      }

      console.log('✅ User marked as active');
      return { success: true };
    } catch (error) {
      console.log('❌ Unexpected error completing onboarding:', error);
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  /**
   * @deprecated Use getUserStatus() instead
   * Ensures that a user profile exists for the given user ID.
   * NOTE: This is no longer needed as profiles are auto-created by database trigger
   */
  async ensureUserProfileExists(_userId: string): Promise<void> {
    console.warn(
      '⚠️ ensureUserProfileExists() is deprecated - profiles are auto-created by trigger'
    );
    // Keep method for backwards compatibility but make it a no-op
  }
}
